import { OVERDRIVE_RULES } from "../../config/gameplay";
import { shortestAngleDistanceDegrees, normalizeDegrees } from "../../utils/math";
import type { ProfileProgress } from "../progression/levels";
import type { OrbitForgeSave, OverdriveEventState } from "../save/types";
import type { ShellState } from "../runtime/types";
import type { FeedbackDraft } from "../runtime/feedback";

export interface OverdriveView {
  readonly unlocked: boolean;
  readonly active: boolean;
  readonly activeKind: OverdriveEventState["activeKind"];
  readonly activeSeconds: number;
  readonly cooldownSeconds: number;
  readonly ready: boolean;
  readonly blockedByStorm: boolean;
  readonly laneLabel: string;
  readonly zoneMultiplier: number;
  readonly prestigeUsesRemaining: number;
  readonly prestigeWindowSecondsRemaining: number;
}

export interface OverdriveAdvanceResult {
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export interface PrestigeOverdriveCommandResult {
  readonly dirty: boolean;
  readonly feedback: readonly FeedbackDraft[];
}

export function buildOverdriveView(save: OrbitForgeSave, progress: ProfileProgress, stormActive: boolean, nowUnixSeconds: number): OverdriveView {
  const state = save.events.overdrive;
  const active = state.activeSeconds > 0;
  const usesRemaining = prestigeUsesRemaining(state, nowUnixSeconds);
  return {
    unlocked: progress.effectiveLevel >= OVERDRIVE_RULES.unlockLevel,
    active,
    activeKind: active ? state.activeKind ?? "normal" : null,
    activeSeconds: state.activeSeconds,
    cooldownSeconds: state.cooldownSeconds,
    ready: progress.effectiveLevel >= OVERDRIVE_RULES.unlockLevel && !active && state.cooldownSeconds <= 0 && !stormActive,
    blockedByStorm: stormActive,
    laneLabel: overdriveLaneLabel(progress, active ? state.activeKind ?? "normal" : null),
    zoneMultiplier: active && state.activeKind === "prestige" ? OVERDRIVE_RULES.prestigeZoneMultiplier : OVERDRIVE_RULES.zoneMultiplier,
    prestigeUsesRemaining: usesRemaining,
    prestigeWindowSecondsRemaining: prestigeWindowSecondsRemaining(state, nowUnixSeconds)
  };
}

export function advanceOverdrive(save: OrbitForgeSave, progress: ProfileProgress, deltaSeconds: number, stormActive: boolean): OverdriveAdvanceResult {
  const state = save.events.overdrive;
  const feedback: FeedbackDraft[] = [];
  let dirty = false;

  if (progress.effectiveLevel < OVERDRIVE_RULES.unlockLevel) {
    return { dirty: false, feedback };
  }

  if (state.activeSeconds > 0) {
    state.activeSeconds = Math.max(0, state.activeSeconds - deltaSeconds);
    dirty = true;
    if (state.activeSeconds <= 0) {
      state.activeKind = null;
      state.cooldownSeconds = OVERDRIVE_RULES.cooldownSeconds;
      feedback.push({ kind: "floating-text", target: "overdrive", frameX: 1690, frameY: 195, color: "white", text: "OVERDRIVE END" });
    }
    return { dirty, feedback };
  }

  if (stormActive) {
    return { dirty, feedback };
  }

  if (state.cooldownSeconds > 0) {
    state.cooldownSeconds = Math.max(0, state.cooldownSeconds - deltaSeconds);
    dirty = true;
  }

  if (state.cooldownSeconds <= 0) {
    startOverdrive(save, "normal");
    dirty = true;
    feedback.push({ kind: "pulse", target: "overdrive" });
    feedback.push({ kind: "floating-text", target: "overdrive", frameX: 1690, frameY: 195, color: "gold", text: "OVERDRIVE" });
  }

  return { dirty, feedback };
}

export function runPrestigeOverdriveCommand(save: OrbitForgeSave, progress: ProfileProgress, stormActive: boolean, nowUnixSeconds: number): PrestigeOverdriveCommandResult {
  syncPrestigeOverdriveWindow(save.events.overdrive, nowUnixSeconds);
  const state = save.events.overdrive;

  if (progress.prestigeLevel <= 0 || stormActive || state.activeSeconds > 0 || prestigeUsesRemaining(state, nowUnixSeconds) <= 0) {
    return {
      dirty: true,
      feedback: [{ kind: "denied", target: "overdrive", frameX: 1690, frameY: 195 }]
    };
  }

  state.prestigeUsesInWindow += 1;
  startOverdrive(save, "prestige");
  return {
    dirty: true,
    feedback: [
      { kind: "pulse", target: "overdrive" },
      { kind: "floating-text", target: "overdrive", frameX: 1690, frameY: 195, color: "gold", text: "PRESTIGE OVERDRIVE" }
    ]
  };
}

export function overdriveLaneMultipliers(progress: ProfileProgress, activeKind: OverdriveEventState["activeKind"]): readonly number[] {
  const laneBoost = progress.effectiveLevel >= OVERDRIVE_RULES.laneBoostUnlockLevel ? 1 : 0;
  if (activeKind === "prestige") {
    return [7, 8, 7, 8, 7];
  }

  if (activeKind === "normal") {
    const advanced = progress.effectiveLevel >= OVERDRIVE_RULES.advancedUnlockLevel;
    return advanced
      ? [4 + laneBoost, 5 + laneBoost, 4 + laneBoost, 5 + laneBoost, 4 + laneBoost]
      : [3 + laneBoost, 4 + laneBoost, 3 + laneBoost, 4 + laneBoost, 3 + laneBoost];
  }

  if (progress.prestigeLevel > 0) {
    return [4, 5, 4, 5, 4];
  }

  return [2 + laneBoost, 3 + laneBoost, 2 + laneBoost, 3 + laneBoost, 2 + laneBoost];
}

export function isAngleInOverdriveZone(shell: ShellState, angleDegrees: number): boolean {
  if (shell.index !== 0) {
    return false;
  }

  const step = 360 / shell.gateCount;
  for (let index = 0; index < shell.gateCount; index += 1) {
    const center = normalizeDegrees(shell.angleDegrees + index * step + step / 2);
    if (shortestAngleDistanceDegrees(angleDegrees, center) <= OVERDRIVE_RULES.zoneHalfDegrees) {
      return true;
    }
  }
  return false;
}

export function overdriveZoneCenters(shell: ShellState): readonly number[] {
  const step = 360 / shell.gateCount;
  return Array.from({ length: shell.gateCount }, (_, index) => normalizeDegrees(shell.angleDegrees + index * step + step / 2));
}

function startOverdrive(save: OrbitForgeSave, activeKind: NonNullable<OverdriveEventState["activeKind"]>): void {
  save.events.overdrive.activeKind = activeKind;
  save.events.overdrive.activeSeconds = OVERDRIVE_RULES.durationSeconds;
  save.events.overdrive.cooldownSeconds = 0;
}

function overdriveLaneLabel(progress: ProfileProgress, activeKind: OverdriveEventState["activeKind"]): string {
  const multipliers = overdriveLaneMultipliers(progress, activeKind);
  return `x${Math.min(...multipliers)}/x${Math.max(...multipliers)}`;
}

function syncPrestigeOverdriveWindow(state: OverdriveEventState, nowUnixSeconds: number): boolean {
  if (nowUnixSeconds - state.prestigeWindowStartedAtUnixSeconds < OVERDRIVE_RULES.prestigeWindowSeconds) {
    return false;
  }

  state.prestigeWindowStartedAtUnixSeconds = nowUnixSeconds;
  state.prestigeUsesInWindow = 0;
  return true;
}

function prestigeUsesRemaining(state: OverdriveEventState, nowUnixSeconds: number): number {
  if (nowUnixSeconds - state.prestigeWindowStartedAtUnixSeconds >= OVERDRIVE_RULES.prestigeWindowSeconds) {
    return OVERDRIVE_RULES.prestigeUsesPerWindow;
  }
  return Math.max(0, OVERDRIVE_RULES.prestigeUsesPerWindow - state.prestigeUsesInWindow);
}

function prestigeWindowSecondsRemaining(state: OverdriveEventState, nowUnixSeconds: number): number {
  const elapsed = nowUnixSeconds - state.prestigeWindowStartedAtUnixSeconds;
  return elapsed >= OVERDRIVE_RULES.prestigeWindowSeconds ? 0 : OVERDRIVE_RULES.prestigeWindowSeconds - elapsed;
}
